import { CellContext } from "@tanstack/react-table";
import type { ViewTableFeatures } from "@design-system/ViewTable/core/table";
import classNames from "classnames";
import {
    memo,
    useCallback,
    useContext,
    useMemo,
    useState,
    type ReactNode,
} from "react";
import { CounterpartyCreationResponse } from "@common/contracts/counterparty.contract";
import { FinancialDocumentExtended } from "@common/types/financialDocument";
import { Counterparty } from "@common/types/counterparty";
import { CheckIcon, TrashIcon } from "@icons";
import { Menu } from "@design-system/Menu/Menu.lib";
import { ViewTableCell } from "@design-system/ViewTable/ViewTableCell";
import { Button } from "@design-system/Button/Button";
import { Tooltip } from "@design-system/Tooltip/Tooltip";
import { HStack } from "@design-system/HStack/HStack";
import { Picker } from "@design-system/Picker/Picker.lib";
import { ConfirmationModal } from "@design-system/Modal/ConfirmationModal";
import { CounterpartyEditorDrawer } from "@components/settings/Directory/CounterpartyEditor/CounterpartyEditorDrawer";
import { AccrualCounterpartyFilePreview } from "./draftDetails/AccrualCounterpartyFilePreview";
import { useViewTableMeta, ViewTableMeta } from "@hooks/view/useViewTableMeta";
import viewTableStyles from "@design-system/ViewTable/ViewTable.module.scss";
import importerStyles from "@components/importer/ImporterPage.module.scss";
import { DocumentName } from "@components/documents/DocumentName";
import { EntityDisplay } from "@components/entity/EntityDisplay";
import { PickerEntityItem } from "@components/entity/PickerEntityItem";
import { Amount } from "@design-system/Amount/Amount";
import { addTimeZoneOffset, formatDateForTransaction } from "@helpers/date";
import { buildCounterpartyPrefillFromPartyHints } from "./buildCounterpartyPrefillFromPartyHints";
import { AccrualImporterDocumentStatus } from "./AccrualImporterDocumentStatus";
import { AccrualImporterCounterpartyAction } from "./AccrualImporterCounterpartyAction";
import { AccrualImporterCounterpartyStatus } from "./AccrualImporterCounterpartyStatus";
import { AccrualImporterContext } from "./accrualImporter.context";
import { useAccrualExtractionRerun } from "./useAccrualExtractionRerun";
import { useAccrualDocumentMove } from "./useAccrualDocumentMove";
import { discardDraftQuestion } from "./accrualImporterDraftGroups";
import { isAccrualRowEligibleForBulkConfirm } from "./accrualDraftBulkEligibility";
import {
    accrualDocumentBeingProcessed,
    getAllAccrualLineItemExtractions,
    resolveAccrualRowExtraction,
    isAccrualExtractionReady,
} from "./accrualImporter.helpers";
import { AccrualImporterViewRow } from "./accrualImporter.types";
import { useRemoveAccrualImporterDocument } from "./useRemoveAccrualImporterDocument";
import { useAccrualImporterEntityEditor } from "./useAccrualImporterEntityEditor";
import { useAccrualImporterCounterpartyEditor } from "./useAccrualImporterCounterpartyEditor";
import {
    AccrualCounterpartyCellDetailChangesButton,
    useAccrualCounterpartyCellHasDetailChanges,
} from "./AccrualCounterpartyCellDetailChanges";
import { useAssignCounterpartyToMatchingDrafts } from "./useAssignCounterpartyToMatchingDrafts";
import statusCellStyles from "@components/invoices/views/invoiceLineItemsView/cells/StatusCell.module.scss";
import styles from "./AccrualDocumentImporter.module.scss";

export interface AccrualImporterTableCallbacks {
    checkSiblings?: (row: AccrualImporterViewRow) => {
        hasTopSibling: boolean;
        hasBottomSibling: boolean;
    };
    confirmDocument?: (financialDocumentId: number) => void;
    confirmingDocumentIds?: readonly number[];
}

export type AccrualImporterTableMeta = ViewTableMeta<
    AccrualImporterTableCallbacks,
    unknown
>;

export const AccrualImporterRowNumberCell = memo(
    ({
        row,
    }: CellContext<ViewTableFeatures, AccrualImporterViewRow, unknown>) => (
        <ViewTableCell>
            <span className={importerStyles.rowNumber}>
                {row.original.rowNumber}
            </span>
        </ViewTableCell>
    ),
);

export const AccrualImporterNameCell = memo(
    ({
        row,
    }: CellContext<ViewTableFeatures, AccrualImporterViewRow, unknown>) => {
        const { isPlaceholder, file, financialDocument } = row.original;

        if (isPlaceholder || row.original.groupLabel) {
            return <ViewTableCell>{row.original.groupLabel}</ViewTableCell>;
        }

        const fileName = financialDocument?.fileName ?? file?.name ?? "";

        return (
            <ViewTableCell>
                <DocumentName
                    fileName={fileName}
                    icon
                    documentId={financialDocument?.id}
                />
            </ViewTableCell>
        );
    },
);

export const AccrualImporterStatusCell = memo(
    ({
        row,
        table,
    }: CellContext<ViewTableFeatures, AccrualImporterViewRow, unknown>) => {
        const { isPlaceholder, file, financialDocument } = row.original;
        const { callbacks } = useViewTableMeta<
            AccrualImporterTableCallbacks,
            unknown
        >(table);
        const { hasTopSibling, hasBottomSibling } = callbacks?.checkSiblings?.(
            row.original,
        ) ?? {
            hasTopSibling: false,
            hasBottomSibling: false,
        };

        if (isPlaceholder || row.original.groupLabel) {
            return <ViewTableCell />;
        }

        return (
            <ViewTableCell>
                <div className={statusCellStyles.statusContent}>
                    <div
                        className={classNames(
                            statusCellStyles.statusContentContainer,
                            {
                                [statusCellStyles.statusContentContainerAdjacentSibling]:
                                    hasBottomSibling,
                                [statusCellStyles.statusContentContainerWithTopLine]:
                                    hasTopSibling,
                            },
                        )}
                    />
                    <AccrualImporterDocumentStatus
                        documentRow={{
                            id: row.original.documentRowId ?? row.original.id,
                            file,
                            financialDocument,
                        }}
                    />
                </div>
            </ViewTableCell>
        );
    },
);

export const AccrualImporterEntityCell = memo(
    ({
        row,
    }: CellContext<ViewTableFeatures, AccrualImporterViewRow, unknown>) => {
        if (
            row.original.groupLabel ||
            row.original.isPlaceholder ||
            !row.original.financialDocument
        ) {
            return <ViewTableCell />;
        }

        return (
            <AccrualImporterEntityCellContent
                documentRowId={row.original.documentRowId ?? row.original.id}
                financialDocument={row.original.financialDocument}
                extractionId={row.original.extractionId}
            />
        );
    },
);

interface AccrualImporterEntityCellContentProps {
    documentRowId: string;
    financialDocument: NonNullable<AccrualImporterViewRow["financialDocument"]>;
    extractionId?: string;
}

const AccrualImporterEntityCellContent = memo(
    ({
        documentRowId,
        financialDocument,
        extractionId,
    }: AccrualImporterEntityCellContentProps) => {
        const { entities, selectedEntity, onEntityChanged, disabledReason } =
            useAccrualImporterEntityEditor({
                financialDocument,
                documentRowId,
                extractionId,
            });

        const isDisabled = disabledReason != null;

        const trigger = useMemo(
            () => (
                <Picker.CellContent
                    addText="No entity"
                    hideAddIcon
                    readOnly={isDisabled}
                    buttonProps={{ tooltip: disabledReason ?? undefined }}
                    value={
                        selectedEntity ? (
                            <EntityDisplay entity={selectedEntity} />
                        ) : undefined
                    }
                    aria-label={selectedEntity?.name ?? undefined}
                />
            ),
            [disabledReason, isDisabled, selectedEntity],
        );

        return (
            <ViewTableCell captureClicks>
                <Picker.Select
                    value={selectedEntity?.id ?? null}
                    onValueChange={(entityId) => {
                        onEntityChanged(
                            entityId != null
                                ? entities.find(
                                      (entity) => entity.id === entityId,
                                  )
                                : undefined,
                        );
                    }}
                    disabled={isDisabled}
                >
                    {trigger}
                    <Picker.Popup>
                        <Picker.List>
                            {entities.map((entity) => (
                                <PickerEntityItem
                                    key={entity.id}
                                    value={entity.id}
                                    entity={entity}
                                />
                            ))}
                        </Picker.List>
                    </Picker.Popup>
                </Picker.Select>
            </ViewTableCell>
        );
    },
);

export const AccrualImporterCounterpartyCell = memo(
    ({
        row,
    }: CellContext<ViewTableFeatures, AccrualImporterViewRow, unknown>) => (
        <AccrualImporterCounterpartyCellFromRow row={row} />
    ),
);

export const AccrualDraftCounterpartyCell = memo(
    ({
        row,
    }: CellContext<ViewTableFeatures, AccrualImporterViewRow, unknown>) => (
        <AccrualImporterCounterpartyCellFromRow row={row} mappedStatus={null} />
    ),
);

const AccrualImporterCounterpartyCellFromRow = memo(
    ({
        row,
        mappedStatus,
    }: {
        row: CellContext<
            ViewTableFeatures,
            AccrualImporterViewRow,
            unknown
        >["row"];
        mappedStatus?: ReactNode;
    }) => {
        if (
            row.original.groupLabel ||
            row.original.isPlaceholder ||
            !row.original.financialDocument
        ) {
            return <ViewTableCell />;
        }

        return (
            <AccrualImporterCounterpartyCellContent
                documentRowId={row.original.documentRowId ?? row.original.id}
                financialDocument={row.original.financialDocument}
                extractionId={row.original.extractionId}
                mappedStatus={mappedStatus}
            />
        );
    },
);

interface AccrualImporterCounterpartyCellContentProps {
    documentRowId: string;
    financialDocument: NonNullable<AccrualImporterViewRow["financialDocument"]>;
    extractionId?: string;
    mappedStatus?: ReactNode;
}

const AccrualImporterCounterpartyCellContent = memo(
    ({
        documentRowId,
        financialDocument,
        extractionId,
        mappedStatus,
    }: AccrualImporterCounterpartyCellContentProps) => {
        const { mode } = useContext(AccrualImporterContext);
        const [showCreateModal, setShowCreateModal] = useState(false);
        const {
            counterparties,
            selectedCounterparty,
            selectedCounterpartyId,
            pendingCounterpartyName,
            matchedCounterparty,
            partyHints,
            canCreateCounterparty,
            onCounterpartyChanged,
            onCounterpartyCreated,
            onConfirmMatchedCounterparty,
            disabledReason,
        } = useAccrualImporterCounterpartyEditor({
            financialDocument,
            documentRowId,
            extractionId,
        });
        const hasDetailChanges = useAccrualCounterpartyCellHasDetailChanges({
            financialDocument,
            mode,
            extractionId,
            counterpartyId: selectedCounterpartyId ?? null,
        });

        const isDisabled = disabledReason != null;
        const isBill = mode === "bills";
        const addText = isBill ? "Select vendor" : "Select customer";
        const createButtonLabel = isBill ? "Create vendor" : "Create customer";
        const counterpartyCreatePrefill = useMemo(
            () =>
                buildCounterpartyPrefillFromPartyHints({
                    hints: partyHints,
                    name: pendingCounterpartyName,
                }),
            [partyHints, pendingCounterpartyName],
        );

        const assignToMatchingDrafts = useAssignCounterpartyToMatchingDrafts();
        const assignToOtherDrafts = useCallback(
            (counterparty: { id: string; name: string }) => {
                void assignToMatchingDrafts({
                    counterparty,
                    extractedName: pendingCounterpartyName,
                    excludeExtractionId: resolveAccrualRowExtraction(
                        financialDocument,
                        mode,
                        extractionId,
                    )?.id,
                });
            },
            [
                assignToMatchingDrafts,
                extractionId,
                financialDocument,
                mode,
                pendingCounterpartyName,
            ],
        );
        const selectCounterparty = useCallback(
            (counterparty: Counterparty) => {
                setShowCreateModal(false);
                onCounterpartyCreated(counterparty.id);
                assignToOtherDrafts(counterparty);
            },
            [assignToOtherDrafts, onCounterpartyCreated],
        );

        const handleCreateModalClosed = useCallback(
            (response?: CounterpartyCreationResponse) => {
                if (response?.counterparty.id) {
                    selectCounterparty(response.counterparty);
                    return;
                }
                setShowCreateModal(false);
            },
            [selectCounterparty],
        );

        const counterpartyCellLabel = (() => {
            if (selectedCounterparty && !hasDetailChanges) {
                return selectedCounterparty.name;
            }
            const highlightedName =
                selectedCounterparty?.name ??
                matchedCounterparty?.name ??
                pendingCounterpartyName;
            return highlightedName ? (
                <span className={styles.pendingCounterpartyName}>
                    {highlightedName}
                </span>
            ) : null;
        })();

        const trigger = useMemo(
            () => (
                <Picker.CellContent
                    addText={addText}
                    hideAddIcon
                    readOnly={isDisabled}
                    buttonProps={{ tooltip: disabledReason ?? undefined }}
                    value={counterpartyCellLabel ?? undefined}
                    aria-label={
                        selectedCounterparty?.name ??
                        pendingCounterpartyName ??
                        undefined
                    }
                />
            ),
            [
                addText,
                counterpartyCellLabel,
                disabledReason,
                isDisabled,
                pendingCounterpartyName,
                selectedCounterparty,
            ],
        );

        const isSuggestion =
            Boolean(pendingCounterpartyName) &&
            selectedCounterpartyId == null &&
            matchedCounterparty == null;
        let counterpartyAction: ReactNode;
        if (hasDetailChanges && selectedCounterpartyId) {
            counterpartyAction = (
                <AccrualCounterpartyCellDetailChangesButton
                    financialDocument={financialDocument}
                    extractionId={extractionId}
                    counterpartyId={selectedCounterpartyId}
                    isDisabled={isDisabled}
                    onCreateNew={() => setShowCreateModal(true)}
                />
            );
        } else if (isSuggestion) {
            counterpartyAction = (
                <Tooltip content="Confirm">
                    <Button
                        variant="accent"
                        size="md"
                        iconOnly
                        aria-label="Confirm"
                        disabled={isDisabled}
                        onClick={() => setShowCreateModal(true)}
                    >
                        <CheckIcon />
                    </Button>
                </Tooltip>
            );
        } else if (matchedCounterparty) {
            counterpartyAction = (
                <Tooltip content="Confirm">
                    <Button
                        variant="accent"
                        size="md"
                        iconOnly
                        aria-label="Confirm suggestion"
                        disabled={isDisabled}
                        onClick={() => {
                            onConfirmMatchedCounterparty(
                                matchedCounterparty.id,
                                matchedCounterparty.name,
                            );
                            assignToOtherDrafts(matchedCounterparty);
                        }}
                    >
                        <CheckIcon />
                    </Button>
                </Tooltip>
            );
        } else {
            counterpartyAction = (
                <AccrualImporterCounterpartyAction
                    canCreate={canCreateCounterparty}
                    createButtonLabel={createButtonLabel}
                    onCreate={() => setShowCreateModal(true)}
                    disabled={isDisabled}
                />
            );
        }

        return (
            <ViewTableCell
                captureClicks
                className={styles.counterpartyCellWrap}
            >
                <HStack
                    gap={8}
                    align="center"
                    className={styles.counterpartyCell}
                >
                    <Picker.Select
                        value={
                            selectedCounterpartyId ??
                            matchedCounterparty?.id ??
                            null
                        }
                        onValueChange={onCounterpartyChanged}
                        disabled={isDisabled}
                    >
                        {trigger}
                        <Picker.Popup>
                            <Picker.List>
                                {counterparties.map((counterparty) => (
                                    <Picker.Item
                                        key={counterparty.id}
                                        value={counterparty.id}
                                    >
                                        {counterparty.name}
                                    </Picker.Item>
                                ))}
                            </Picker.List>
                        </Picker.Popup>
                    </Picker.Select>
                    {mappedStatus !== undefined ? (
                        mappedStatus
                    ) : (
                        <AccrualImporterCounterpartyStatus
                            isMapped={selectedCounterpartyId != null}
                        />
                    )}
                    <div className={styles.counterpartyAction}>
                        {counterpartyAction}
                    </div>
                </HStack>
                <CounterpartyEditorDrawer
                    open={showCreateModal}
                    isCreating
                    onClose={() => handleCreateModalClosed()}
                    onCreated={handleCreateModalClosed}
                    onEdited={selectCounterparty}
                    onLocalized={selectCounterparty}
                    fields="extracted"
                    initialValues={counterpartyCreatePrefill.initialValues}
                    seedContacts={counterpartyCreatePrefill.contacts}
                    seedAddresses={counterpartyCreatePrefill.addresses}
                    sidePreview={
                        <AccrualCounterpartyFilePreview
                            financialDocument={financialDocument}
                        />
                    }
                />
            </ViewTableCell>
        );
    },
);

export const AccrualImporterDocumentNumberCell = memo(
    ({
        row,
    }: CellContext<ViewTableFeatures, AccrualImporterViewRow, unknown>) => {
        const { mode } = useContext(AccrualImporterContext);

        if (row.original.isPlaceholder || !row.original.financialDocument) {
            return <ViewTableCell />;
        }

        const extraction = resolveAccrualRowExtraction(
            row.original.financialDocument,
            mode,
            row.original.extractionId,
        );

        return (
            <ViewTableCell>
                {extraction?.documentNumber ?? (
                    <span className={styles.placeholder}>-</span>
                )}
            </ViewTableCell>
        );
    },
);

export const AccrualImporterDocumentDateCell = memo(
    ({
        row,
    }: CellContext<ViewTableFeatures, AccrualImporterViewRow, unknown>) => {
        const { mode } = useContext(AccrualImporterContext);

        if (row.original.isPlaceholder || !row.original.financialDocument) {
            return <ViewTableCell />;
        }

        const extraction = resolveAccrualRowExtraction(
            row.original.financialDocument,
            mode,
            row.original.extractionId,
        );
        const documentDate = extraction?.documentDate;

        return (
            <ViewTableCell>
                {documentDate ? (
                    formatDateForTransaction(
                        addTimeZoneOffset(new Date(documentDate)),
                    )
                ) : (
                    <span className={styles.placeholder}>-</span>
                )}
            </ViewTableCell>
        );
    },
);

export const AccrualImporterTotalCell = memo(
    ({
        row,
    }: CellContext<ViewTableFeatures, AccrualImporterViewRow, unknown>) => {
        const { mode } = useContext(AccrualImporterContext);

        if (row.original.isPlaceholder || !row.original.financialDocument) {
            return <ViewTableCell />;
        }

        const extraction = resolveAccrualRowExtraction(
            row.original.financialDocument,
            mode,
            row.original.extractionId,
        );
        const lineItem = row.original.lineItemId
            ? extraction?.lineItems.find(
                  (item) => item.lineId === row.original.lineItemId,
              )
            : undefined;
        const amount = lineItem?.amount ?? extraction?.totalAmount ?? null;

        return (
            <ViewTableCell>
                {amount != null ? (
                    <Amount amount={amount} />
                ) : (
                    <span className={styles.placeholder}>-</span>
                )}
            </ViewTableCell>
        );
    },
);

export const AccrualImporterActionsCell = memo(
    ({
        row,
        table,
    }: CellContext<ViewTableFeatures, AccrualImporterViewRow, unknown>) => {
        const { callbacks } = useViewTableMeta<
            AccrualImporterTableCallbacks,
            unknown
        >(table);
        const documentRow = row.original;

        if (documentRow.isPlaceholder || documentRow.groupLabel) {
            return <ViewTableCell />;
        }

        return (
            <AccrualImporterActionsCellContent
                documentRowId={documentRow.documentRowId ?? documentRow.id}
                extractionId={documentRow.extractionId}
                file={documentRow.file}
                financialDocument={documentRow.financialDocument}
                onConfirmDocument={callbacks?.confirmDocument}
                confirmingDocumentIds={callbacks?.confirmingDocumentIds}
            />
        );
    },
);

interface AccrualImporterActionsCellContentProps {
    documentRowId: string;
    extractionId?: string;
    file?: File;
    financialDocument?: AccrualImporterViewRow["financialDocument"];
    onConfirmDocument?: (financialDocumentId: number) => void;
    confirmingDocumentIds?: readonly number[];
}

const AccrualImporterActionsCellContent = memo(
    ({
        documentRowId,
        extractionId,
        file,
        financialDocument,
        onConfirmDocument,
        confirmingDocumentIds,
    }: AccrualImporterActionsCellContentProps) => {
        const { setPreviewedRowId } = useContext(AccrualImporterContext);
        const { removeDocument, isRemoving } = useRemoveAccrualImporterDocument(
            {
                id: documentRowId,
                file,
                financialDocument,
            },
        );
        const isReady = financialDocument
            ? isAccrualExtractionReady(financialDocument)
            : false;
        const isReadyForImport = isAccrualRowEligibleForBulkConfirm({
            id: documentRowId,
            file,
            financialDocument,
        });

        if (!financialDocument) {
            return (
                <ViewTableCell captureClicks>
                    <span
                        className={classNames(
                            importerStyles.actionsColumn,
                            styles.rowActions,
                            viewTableStyles.showOnHover,
                        )}
                    >
                        <Button
                            variant="secondary"
                            iconOnly
                            size="sm"
                            disabled={isRemoving}
                            onClick={removeDocument}
                            aria-label="Discard document"
                        >
                            <TrashIcon />
                        </Button>
                    </span>
                </ViewTableCell>
            );
        }

        return (
            <ViewTableCell captureClicks>
                <span
                    className={classNames(
                        importerStyles.actionsColumn,
                        styles.rowActions,
                        viewTableStyles.showOnHover,
                    )}
                >
                    {isReadyForImport && onConfirmDocument ? (
                        <Button
                            variant="primary"
                            size="sm"
                            disabled={confirmingDocumentIds?.includes(
                                financialDocument.id,
                            )}
                            onClick={() =>
                                onConfirmDocument(financialDocument.id)
                            }
                        >
                            Confirm
                        </Button>
                    ) : (
                        <Button
                            variant="primary"
                            size="sm"
                            disabled={!isReady}
                            onClick={() =>
                                setPreviewedRowId(documentRowId, extractionId)
                            }
                        >
                            Review
                        </Button>
                    )}
                    <AccrualImporterRowActionsMenu
                        financialDocument={financialDocument}
                        extractionId={extractionId}
                        onRemove={removeDocument}
                        isRemoving={isRemoving}
                    />
                </span>
            </ViewTableCell>
        );
    },
);

interface AccrualImporterRowActionsMenuProps {
    financialDocument: FinancialDocumentExtended;
    extractionId?: string;
    onRemove: () => void;
    isRemoving: boolean;
}

const AccrualImporterRowActionsMenu = memo(
    ({
        financialDocument,
        extractionId,
        onRemove,
        isRemoving,
    }: AccrualImporterRowActionsMenuProps) => {
        const { mode } = useContext(AccrualImporterContext);
        const { requestRerun, rerunConfirm, isRerunning } =
            useAccrualExtractionRerun();
        const { moveDocuments, moveLabel, isMoving } = useAccrualDocumentMove();
        const [isRemoveConfirmOpen, setIsRemoveConfirmOpen] = useState(false);
        const isProcessing = accrualDocumentBeingProcessed(financialDocument);
        const itemLabel = mode === "invoices" ? "invoice" : "bill";
        const omittedExtractionCount = extractionId
            ? getAllAccrualLineItemExtractions(financialDocument, mode).filter(
                  (extraction) => extraction.id !== extractionId,
              ).length
            : 0;
        const requestRemove = () => {
            if (omittedExtractionCount > 0) {
                setIsRemoveConfirmOpen(true);
                return;
            }
            onRemove();
        };

        return (
            <>
                <Menu.More
                    aria-label="Document actions"
                    size="md"
                    align="start"
                >
                    {!isRerunning && !isProcessing && (
                        <Menu.Item
                            onClick={() => requestRerun([financialDocument])}
                        >
                            Rerun extraction
                        </Menu.Item>
                    )}
                    {!isMoving && !isProcessing && (
                        <Menu.Item
                            onClick={() => moveDocuments([financialDocument])}
                        >
                            {moveLabel}
                        </Menu.Item>
                    )}
                    <Menu.Item
                        onClick={requestRemove}
                        disabled={isRemoving}
                        destructive
                    >
                        {isRemoving ? "Discarding..." : "Discard"}
                    </Menu.Item>
                </Menu.More>
                {rerunConfirm}
                <ConfirmationModal
                    show={isRemoveConfirmOpen}
                    title="Discard draft?"
                    question={discardDraftQuestion({
                        fileCount: 1,
                        omittedExtractionCount,
                        itemLabel,
                    })}
                    yes="Discard"
                    yesBtnVariant="destructive"
                    no="Cancel"
                    onHide={() => setIsRemoveConfirmOpen(false)}
                    onConfirm={() => {
                        setIsRemoveConfirmOpen(false);
                        onRemove();
                    }}
                />
            </>
        );
    },
);
